import {PIXI} from '@colonist/pixi'
import FPS from 'yy-fps'
import {GameColor} from '../GameColor.ts'
import {UIGameResponsiveController} from './UIGameResponsiveController.ts'
import {StorageWrapper} from '../StorageWrapper.ts'
import {type UIGameManager} from './UIGameManager.ts'
import {UICanvasController} from '../ui_canvas_engine/UICanvasController.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {isAffectedBySafariWebGlBug} from '../ui_utils/ClientUtils.ts'
import {CA} from '../analytics/Analytics.ts'
import {TimeUtils} from '@colonist/utils'
import * as Sentry from '@sentry/browser'

export abstract class UIGameCanvasController extends UICanvasController {
    protected readonly uiGameManager: UIGameManager
    readonly responsiveController: UIGameResponsiveController
    private readonly resizeScreen: () => void
    abstract get chatWidth(): number
    abstract get feedsHeight(): number
    abstract get popupWidth(): number
    abstract get bottomViewHeight(): number
    fps?: FPS

    private backgroundSprite?: PIXI.Sprite

    constructor(uiGameManager: UIGameManager) {
        super()
        this.uiGameManager = uiGameManager
        UIGameCanvasController.setInitialValues()
        this.createPixiApplication()
        this.resizeScreen = () => this.responsiveController.resizeScreen()
    }

    private static setInitialValues() {
        const permissibleValuesKeyboardShortcuts = [false, true]
        const keyboardShortcuts = StorageWrapper.getKeyboardShortcuts()
        if(keyboardShortcuts == undefined || !permissibleValuesKeyboardShortcuts.includes(keyboardShortcuts)) {
            StorageWrapper.setKeyboardShortcuts(true)
        }

        const permissibleValuesAntialias = [false, true]
        const antialias = StorageWrapper.getAntialias()
        if(antialias == undefined || !permissibleValuesAntialias.includes(antialias)) {
            StorageWrapper.setAntialias(window.devicePixelRatio * window.innerWidth <= 2000)
        }

        const permissibleValuesResolution = [0.5, 1, 1.5, 2]
        const resolution = StorageWrapper.getResolution()
        if(resolution == undefined || !permissibleValuesResolution.includes(resolution)) {
            const defaultResolution = Math.min(2, window.devicePixelRatio)
            StorageWrapper.setResolution(defaultResolution)
        }

        const permissibleValuesAnimation = [false, true]
        const animationStatus = StorageWrapper.getAnimation()
        if(animationStatus == undefined || !permissibleValuesAnimation.includes(animationStatus)) {
            StorageWrapper.setAnimation(true)
        }
    }

    private createPixiApplication() {
        const antialias = StorageWrapper.getAntialias() ?? false
        const resolution = UIGameCanvasController.getResolution()
        const powerPref = StorageWrapper.getPowerPreference()

        const options: Partial<PIXI.IApplicationOptions> = {
            antialias: isAffectedBySafariWebGlBug() ? false : antialias,
            resolution: resolution,
            backgroundColor: GameColor.getColorHexValue(GameColor.Color.Sea),
            powerPreference: powerPref,
            hello: false,
        }

        super.createPIXIApplication(options)

        this.fps = new FPS()
        this.fps.div.style.display = 'none'
    }

    private static getResolution(): number {
        const resolution = StorageWrapper.getResolution()
        if(resolution == undefined || resolution == null) return 1

        const resolutionScale = UIGameResponsiveController.resolutionScale
        const scaledResolution = resolution * resolutionScale

        if(!UIUtilsEjsVariables.isUserOnMobile) return scaledResolution

        const allowedScalesInMobile = [0.5, 1, 1.5, 2]
        if(allowedScalesInMobile.includes(resolution)) return resolution
        return 2
    }

    builtGame(): boolean {
        if(this.app.view.style.display == 'block') return true
        return false
    }

    setBackgroundSprite(texture: PIXI.Texture) {
        this.backgroundSprite?.destroy()

        this.backgroundSprite = new PIXI.Sprite(texture)
        this.backgroundSprite.width = this.app.view.width
        this.backgroundSprite.height = this.app.view.height
        this.app.stage.addChildAt(this.backgroundSprite, 0)
    }

    private buildCanvas() {
        this.app.view.style.position = 'absolute'
        this.app.view.style.display = 'block'
        this.app.view.style.userSelect = 'none'
    }

    private buildDocument() {
        document.body.style.backgroundColor = GameColor.getColor(GameColor.Color.PrimaryBlue0)
        document.body.appendChild(this.app.view)
        document.body.style.overflow = 'hidden'
        if(document.documentElement != undefined) document.documentElement.style.overflow = 'hidden'
        window.addEventListener('resize', this.resizeScreen)
    }

    removeResizeListener() {
        window.removeEventListener('resize', this.resizeScreen)
    }

    buildGame() {
        void Sentry.getReplay()?.stop()
        if(this.builtGame()) return
        this.buildDocument()
        this.buildCanvas()
        this.setupGameContainers()
        this.enableFPSAnalytics()

        this.responsiveController.buildRenderer()
        this.responsiveController.resizeScreen()

        if(this.fps != undefined && !UIUtilsEjsVariables.isProduction) this.fps.div.style.display = 'block'
    }

    abstract setupGameContainers()

    // Jan 27, 2021. Jeff: PIXI.App doesn't start loading until you have a child
    // This is a hacky way to start the loading for better UX. There might be a better way?
    protected kickOffAppLoading() {
        const blankContainer = new PIXI.Container()
        this.uiGameManager.canvasController.app.stage.addChild(blankContainer)
        blankContainer.parent.removeChild(blankContainer)
    }

    protected addTicker() {
        this.app.ticker.add(() => {
            const {deltaTime, elapsedMS} = this.app.ticker
            this.uiGameManager.eventController.gameTick(deltaTime, elapsedMS)
            this.fps?.frame()
        }, undefined, PIXI.UPDATE_PRIORITY.NORMAL)
    }

    private enableFPSAnalytics() {
        const PROPORTION_OF_CLIENTS_TO_SEND_FPS = 0.05 // 5% of clients will send fps data
        const isFPSAnalyticsEnabled = Math.random() < PROPORTION_OF_CLIENTS_TO_SEND_FPS
        if(!isFPSAnalyticsEnabled) return

        const FPS_ANALYTICS_INTERVAL = TimeUtils.MILLISECONDS * 20 // 20 seconds
        setInterval(() => this.sendFPSAnalyticEvent(), FPS_ANALYTICS_INTERVAL)
    }

    private sendFPSAnalyticEvent() {
        const fps = this.fps?.lastFPS
        if(fps == undefined) return

        CA.sendGoogleAnalyticsEvent(CA.Category.Game, CA.Label.GameFramesPerSecond, fps.toString())
    }

    resizeBackgroundSprite() {
        if(this.backgroundSprite == undefined) return
        this.backgroundSprite.width = this.app.view.width
        this.backgroundSprite.height = this.app.view.height
    }
}
